/**
 * UserAssistance (v4.7.72)
 *
 * Authenticated-admin-only impersonation surface. v4.7.72 replaces the
 * blind-text-input form with an AJAX-driven target picker, MFA pre-flight,
 * visual digit-box authenticator entry, and inline step-by-step progress
 * feedback. Server contract is unchanged from v4.7.65/v4.7.66 \u2014 the wire
 * payload still goes through masterAdminLogin() and createServerSession().
 *
 * Flow:
 *   1. On mount: pre-flights the admin's MFA state via /api/admin/generate-pin
 *      (action=check_mfa). If TOTP is not enrolled the form is replaced
 *      with a banner directing the admin to enrol under My Security first.
 *   2. Admin types into the target picker; we debounce 350ms then call
 *      /api/admin/users?search=\u2026 for the top 8 ranked candidates.
 *      Suspended/frozen accounts are explicitly blocked at the picker so
 *      the admin cannot start a session for a frozen user.
 *   3. Admin enters master password (show/hide toggle) and a 6-digit code
 *      across six separate digit boxes (auto-advance, Backspace recall,
 *      paste support). A toggle switches to a single 8-digit backup field.
 *   4. Submit runs a four-step inline progress display (password / MFA /
 *      session / redirect). Each step's spinner resolves to \u2713 or \u2717.
 *   5. masterAdminLogin() re-verifies the master password (timing-safe),
 *      the TOTP via verifyMFA(), enforces self-impersonation block,
 *      writes the audit row, and returns an HMAC-signed loginToken.
 *   6. createServerSession() establishes the impersonation session and we
 *      hard-redirect to / so App.tsx hydrates from the new session.
 */
import React, { useState, useEffect, useRef } from 'react'
import { masterAdminLogin } from '../../lib/masterAdminAccess'
import { createServerSession, revokeServerSession, getServerSessionId } from '../../lib/serverSession'

interface PickerUser {
  id: number
  username: string
  email: string
  full_name?: string | null
  account_status?: string | null
}

interface Props { currentUser?: any }

type StepState = 'idle' | 'running' | 'ok' | 'err'
type MfaProbeStatus = 'probing' | 'ready' | 'no-totp' | 'error'

// Inline spinner \u2014 matches admin-dashboard visual idiom.
const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
)

// Single row in the inline progress card.
const StepRow: React.FC<{ state: StepState; label: string }> = ({ state, label }) => {
  let icon: React.ReactNode = <span className="text-gray-600">○</span>
  let textCls = 'text-gray-400'
  if (state === 'running') { icon = <Spinner />; textCls = 'text-blue-300' }
  if (state === 'ok')      { icon = <span className="text-green-400">✓</span>; textCls = 'text-green-300' }
  if (state === 'err')     { icon = <span className="text-red-400">✗</span>; textCls = 'text-red-300' }
  return (
    <div className={`flex items-center gap-2 ${textCls}`}>
      <span className="w-5 inline-flex justify-center">{icon}</span>
      <span>{label}</span>
    </div>
  )
}

export const UserAssistance: React.FC<Props> = ({ currentUser }) => {
  const adminEmail = currentUser?.adminUser?.email || currentUser?.email || ''
  const adminId = currentUser?.adminUser?.id || currentUser?.id || null

  // --- MFA pre-flight against the admin's own account ---
  const [mfaProbe, setMfaProbe] = useState<{ status: MfaProbeStatus; message?: string }>({ status: 'probing' })

  // --- Target user picker (debounced AJAX) ---
  const [targetQuery, setTargetQuery] = useState('')
  const [targetResults, setTargetResults] = useState<PickerUser[]>([])
  const [searching, setSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [selectedUser, setSelectedUser] = useState<PickerUser | null>(null)
  const debounceRef = useRef<number | null>(null)

  // --- Credentials ---
  const [masterPassword, setMasterPassword] = useState('')
  const [showMaster, setShowMaster] = useState(false)
  const [useBackup, setUseBackup] = useState(false)
  const [totpDigits, setTotpDigits] = useState<string[]>(['','','','','',''])
  const [backupCode, setBackupCode] = useState('')
  const totpRefs = useRef<Array<HTMLInputElement | null>>([])

  // --- Progress / submission state ---
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [steps, setSteps] = useState<{ password: StepState; mfa: StepState; session: StepState; redirect: StepState }>({
    password: 'idle', mfa: 'idle', session: 'idle', redirect: 'idle'
  })

  const totpValue = useBackup ? backupCode : totpDigits.join('')
  const totpExpectedLen = useBackup ? 8 : 6
  const totpComplete = totpValue.length === totpExpectedLen

  // Pre-flight: confirm the signed-in admin has TOTP enrolled before allowing
  // any further interaction. The server-side gate (master-admin-login.js +
  // verifyMFA) is the source of truth; this is purely for clean UX so an
  // admin without TOTP gets a clear "enrol first" banner instead of a 401
  // surprise after typing the master password.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!adminEmail) {
        if (!cancelled) setMfaProbe({ status: 'error', message: 'Could not resolve your admin email.' })
        return
      }
      try {
        const sessionId = getServerSessionId()
        const r = await fetch('/api/admin/generate-pin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(sessionId ? { 'X-Session-Id': sessionId } : {}) },
          body: JSON.stringify({ action: 'check_mfa', adminEmail })
        })
        const data = await r.json()
        if (cancelled) return
        if (!data?.success) {
          setMfaProbe({ status: 'error', message: data?.error || 'Failed to load authenticator status.' })
          return
        }
        setMfaProbe({ status: data.totpEnabled ? 'ready' : 'no-totp' })
      } catch (e: any) {
        if (!cancelled) setMfaProbe({ status: 'error', message: e?.message || 'Network error.' })
      }
    })()
    return () => { cancelled = true }
  }, [adminEmail])

  // Debounced live search against /api/admin/users. We cap at 8 ranked
  // matches; the server-side relevance ranker already does the heavy lifting
  // (lib/searchRelevanceServer.js) so the dropdown reflects exact-match
  // priority over substring noise.
  useEffect(() => {
    if (selectedUser) return
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    const q = targetQuery.trim()
    if (q.length < 2) { setTargetResults([]); setShowResults(false); return }
    debounceRef.current = window.setTimeout(async () => {
      setSearching(true)
      try {
        const sessionId = getServerSessionId()
        const params = new URLSearchParams({ page: '1', pageSize: '8', search: q })
        const r = await fetch(`/api/admin/users?${params.toString()}`, {
          headers: sessionId ? { 'X-Session-Id': sessionId } : {}
        })
        const data = await r.json()
        const rows: PickerUser[] = (data?.users || []).map((u: any) => ({
          id: u.id,
          username: u.username,
          email: u.email,
          full_name: u.full_name,
          account_status: u.account_status
        }))
        setTargetResults(rows)
        setShowResults(true)
      } catch {
        setTargetResults([])
        setShowResults(true)
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }
  }, [targetQuery, selectedUser])

  const handlePickUser = (u: PickerUser) => {
    // Block suspension/freeze impersonation at the picker. The server-side
    // fraud-freeze trigger (v4.7.63) is still the authority, but blocking
    // here avoids burning an audit row + an MFA attempt for an action that
    // will fail anyway.
    if (u.account_status && /suspend|frozen|fraud/i.test(u.account_status)) {
      setError(`${u.username} is ${u.account_status}. Frozen accounts cannot be impersonated.`)
      return
    }
    setSelectedUser(u)
    setShowResults(false)
    setError(null)
    // Auto-focus the first OTP digit so the admin can keep typing without
    // reaching for the mouse \u2014 the dominant UX complaint with the v4.7.66 form.
    setTimeout(() => { if (!useBackup) totpRefs.current[0]?.focus() }, 60)
  }

  const handleClearUser = () => {
    setSelectedUser(null)
    setTargetQuery('')
    setTargetResults([])
    setShowResults(false)
    setError(null)
  }

  // Six independent digit boxes with auto-advance + Backspace recall + paste.
  const handleTotpDigit = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1)
    setTotpDigits(prev => {
      const next = [...prev]
      next[idx] = digit
      return next
    })
    if (digit && idx < 5) totpRefs.current[idx + 1]?.focus()
  }
  const handleTotpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !totpDigits[idx] && idx > 0) {
      totpRefs.current[idx - 1]?.focus()
    }
  }
  const handleTotpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    e.preventDefault()
    const next = ['','','','','','']
    pasted.split('').forEach((d, i) => { next[i] = d })
    setTotpDigits(next)
    const focusIdx = Math.min(pasted.length, 5)
    totpRefs.current[focusIdx]?.focus()
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e?.preventDefault) e.preventDefault()
    setError(null)
    if (!selectedUser) { setError('Select a target user first.'); return }
    if (!masterPassword) { setError('Enter the master assistance password.'); return }
    if (!totpComplete) {
      setError(`Enter your ${totpExpectedLen}-digit ${useBackup ? 'backup' : 'authenticator'} code.`)
      return
    }

    setBusy(true)
    setSteps({ password: 'running', mfa: 'idle', session: 'idle', redirect: 'idle' })

    try {
      const result = await masterAdminLogin(selectedUser.email, masterPassword, adminEmail, totpValue)
      if (!result.success || !result.user) {
        // Coarse routing of server error \u2192 the right step row. The server
        // returns a stable `code` (MFA_INVALID / MFA_LOCKED / MFA_TOTP_REQUIRED
        // / MFA_NOT_ENROLLED) which we use when present; otherwise fall back
        // to keyword sniffing against the human message.
        const code = (result as any).code as string | undefined
        const msg = (result.error || '').toLowerCase()
        const isMfaErr = code?.startsWith('MFA_') || /totp|authenticator|backup|mfa|locked/.test(msg)
        if (isMfaErr) setSteps(s => ({ ...s, password: 'ok', mfa: 'err' }))
        else setSteps(s => ({ ...s, password: 'err' }))
        setError(result.error || 'Master admin login failed.')
        if (isMfaErr) { setTotpDigits(['','','','','','']); setBackupCode('') }
        setBusy(false)
        return
      }

      const targetDb = result.user?.database_user
      if (!targetDb?.id || !targetDb?.email || !result.loginToken) {
        setSteps(s => ({ ...s, password: 'ok', mfa: 'ok', session: 'err' }))
        setError('Server response missing impersonation token \u2014 please retry.')
        setBusy(false)
        return
      }

      setSteps(s => ({ ...s, password: 'ok', mfa: 'ok', session: 'running' }))
      try { await revokeServerSession() } catch { /* non-fatal */ }
      const sess = await createServerSession(targetDb.id, targetDb.email, 'email', undefined, result.loginToken)
      if (!sess.success) {
        setSteps(s => ({ ...s, session: 'err' }))
        setError(sess.error || 'Failed to establish impersonation session.')
        setBusy(false)
        return
      }

      setSteps(s => ({ ...s, session: 'ok', redirect: 'running' }))
      localStorage.setItem('aureus_user', JSON.stringify(result.user))
      localStorage.setItem('aureus_auth_timestamp', Date.now().toString())
      // Small delay so the operator visually sees all four checkmarks before
      // the hard reload yanks the page.
      setTimeout(() => window.location.assign('/'), 400)
    } catch (err: any) {
      setError(err?.message || 'Network error.')
      setSteps(s => ({
        password: s.password === 'running' ? 'err' : s.password,
        mfa:      s.mfa      === 'running' ? 'err' : s.mfa,
        session:  s.session  === 'running' ? 'err' : s.session,
        redirect: 'idle'
      }))
      setBusy(false)
    }
  }

  const formDisabled = busy || mfaProbe.status === 'no-totp' || mfaProbe.status === 'probing'
  const submitDisabled = formDisabled || !selectedUser || !masterPassword || !totpComplete

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <span>🆘</span> User Assistance
          </h2>
          <p className="text-sm text-gray-400 mt-1">Sign in as a user to help them complete actions. Every step is audited.</p>
        </div>
        <div className="text-right text-xs text-gray-400">
          <div>Signed in as <strong className="text-gray-200">{adminEmail || 'unknown'}</strong></div>
          {adminId ? <div>admin id {adminId}</div> : null}
        </div>
      </div>

      {/* MFA pre-flight banner */}
      {mfaProbe.status === 'probing' && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3 mb-4 text-blue-200 text-sm flex items-center gap-2">
          <Spinner /> Checking your authenticator status…
        </div>
      )}
      {mfaProbe.status === 'no-totp' && (
        <div className="bg-red-900/20 border border-red-500/40 rounded p-4 mb-4 text-red-200 text-sm">
          <div className="font-semibold mb-1">🚫 Authenticator not enrolled</div>
          <div>You must enrol an authenticator app under <strong>My Security → MFA</strong> before you can use User Assistance.</div>
        </div>
      )}
      {mfaProbe.status === 'error' && (
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 mb-4 text-yellow-200 text-sm">
          ⚠️ Couldn’t verify authenticator status{mfaProbe.message ? `: ${mfaProbe.message}` : ''}. You can still proceed, but the request may fail server-side.
        </div>
      )}

      {/* Privilege warning */}
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 mb-6 text-yellow-200 text-xs space-y-1">
        <div>This is a high-privilege impersonation tool. Every attempt is logged with your admin id, the target user, MFA result, IP, and user-agent.</div>
        <div>Server-side read-only is enforced for withdrawals, MFA changes, profile/wallet updates, and KYC mutations under impersonated sessions. Share purchases are permitted to assist the user.</div>
        <div>You will be logged out of your admin session when assistance starts.</div>
      </div>

      {/* Section 1: target user picker (live AJAX) */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-200 mb-2">
          1. Target user
        </label>
        {!selectedUser ? (
          <div className="relative">
            <input
              type="text"
              value={targetQuery}
              onChange={(e) => setTargetQuery(e.target.value)}
              onFocus={() => { if (targetResults.length) setShowResults(true) }}
              placeholder="Search by username, email, full name, or ID…"
              disabled={formDisabled}
              autoComplete="off"
              className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-3 pr-10 focus:border-yellow-500 focus:outline-none disabled:opacity-50"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300"><Spinner /></div>
            )}
            {showResults && targetResults.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full bg-gray-900 border border-gray-600 rounded shadow-2xl max-h-80 overflow-y-auto">
                {targetResults.map(u => {
                  const frozen = !!(u.account_status && /suspend|frozen|fraud/i.test(u.account_status))
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handlePickUser(u)}
                      disabled={frozen}
                      className={`w-full text-left px-3 py-2 border-b border-gray-800 last:border-b-0 flex items-center justify-between gap-3 ${frozen ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-800'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium truncate">{u.full_name || u.username}</div>
                        <div className="text-gray-400 text-xs truncate">@{u.username} · {u.email}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <div className="text-gray-500 text-xs">id {u.id}</div>
                        {u.account_status && u.account_status !== 'active' ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wide ${frozen ? 'bg-red-900/40 text-red-300' : 'bg-gray-700 text-gray-300'}`}>
                            {u.account_status}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
            {showResults && !searching && targetResults.length === 0 && targetQuery.trim().length >= 2 && (
              <div className="absolute z-20 top-full mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-gray-400 text-sm">
                No users match “{targetQuery}”.
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">Type at least 2 characters. Results rank exact matches first.</p>
          </div>
        ) : (
          <div className="bg-gray-900 border border-green-500/40 rounded p-4 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-green-300 text-[10px] uppercase tracking-wider mb-1">Selected</div>
              <div className="text-white text-base font-semibold truncate">{selectedUser.full_name || selectedUser.username}</div>
              <div className="text-gray-400 text-xs truncate">@{selectedUser.username} · {selectedUser.email} · id {selectedUser.id}</div>
              {selectedUser.account_status ? (
                <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded uppercase tracking-wide ${selectedUser.account_status === 'active' ? 'bg-green-900/40 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                  {selectedUser.account_status}
                </span>
              ) : null}
            </div>
            <button type="button" onClick={handleClearUser} disabled={busy} className="text-blue-400 hover:text-blue-300 text-sm shrink-0 disabled:opacity-50">
              Change
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {/* Section 2: master password */}
        <div>
          <label className="block text-sm font-semibold text-gray-200 mb-2">
            2. Master assistance password
          </label>
          <div className="flex gap-2">
            <input
              type={showMaster ? 'text' : 'password'}
              value={masterPassword}
              onChange={(e) => setMasterPassword(e.target.value)}
              disabled={formDisabled}
              autoComplete="new-password"
              placeholder="••••••••••••"
              className="flex-1 bg-gray-900 border border-gray-700 text-white rounded px-3 py-3 focus:border-yellow-500 focus:outline-none disabled:opacity-50"
            />
            <button type="button" onClick={() => setShowMaster(s => !s)} disabled={busy} className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 rounded disabled:opacity-50">
              {showMaster ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {/* Section 3: OTP digit boxes or backup code */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-200">
              3. {useBackup ? 'Your 8-digit backup code' : 'Your 6-digit authenticator code'}
            </label>
            <button
              type="button"
              onClick={() => { setUseBackup(b => !b); setBackupCode(''); setTotpDigits(['','','','','','']); setError(null) }}
              disabled={busy}
              className="text-xs text-blue-400 hover:underline disabled:opacity-50"
            >
              {useBackup ? 'Back to authenticator app' : 'Lost device? Use a backup code'}
            </button>
          </div>
          {!useBackup ? (
            <div className="flex gap-2 justify-between">
              {totpDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => { totpRefs.current[i] = el }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleTotpDigit(i, e.target.value)}
                  onKeyDown={(e) => handleTotpKeyDown(i, e)}
                  onPaste={i === 0 ? handleTotpPaste : undefined}
                  disabled={formDisabled}
                  className="w-12 h-14 sm:w-14 sm:h-16 bg-gray-900 border border-gray-700 text-white rounded text-center text-2xl font-mono focus:border-yellow-500 focus:outline-none disabled:opacity-50"
                />
              ))}
            </div>
          ) : (
            <input
              type="text"
              inputMode="numeric"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              disabled={formDisabled}
              maxLength={8}
              placeholder="00000000"
              className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-3 text-center text-2xl font-mono tracking-[0.5em] focus:border-yellow-500 focus:outline-none disabled:opacity-50"
            />
          )}
        </div>

        {/* Inline error (after step routing) */}
        {error ? (
          <div className="bg-red-900/20 border border-red-500/40 rounded p-3 text-red-200 text-sm">{error}</div>
        ) : null}

        {/* Inline step progress \u2014 visible during submit and after final state */}
        {(busy || steps.password !== 'idle') && (
          <div className="bg-gray-900/60 border border-gray-700 rounded p-4 space-y-2 text-sm">
            <StepRow state={steps.password} label="Verifying master password" />
            <StepRow state={steps.mfa}      label="Verifying authenticator code" />
            <StepRow state={steps.session}  label="Creating impersonation session" />
            <StepRow state={steps.redirect} label="Redirecting to assisted dashboard" />
          </div>
        )}

        <button
          type="submit"
          disabled={submitDisabled}
          className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded px-4 py-3 transition flex items-center justify-center gap-2"
        >
          {busy ? <><Spinner /> Working…</> : 'Start Assistance Session'}
        </button>
      </form>
    </div>
  )
}

export default UserAssistance
